import { useAuthStore } from "@/stores/useAuthStore";
import { celebrationKey, celebrationRank, type CelebrationKind } from "./celebrationRules";
import { markSeen } from "./celebrationSeen";

/**
 * The one door every celebration goes through. Callers are success paths that
 * already committed money or data, so nothing here may throw back into them.
 * Dedupe is by key across BOTH sides: the actor's call marks the key seen for
 * the current user, so their own notification of the same event (the creator
 * also receives quest_completed) never replays it.
 */
export interface CelebrationEvent {
  kind: CelebrationKind;
  entityId: string;
  key: string;
  source: "action" | "notification";
  /** The viewer's own work was just approved: point them at their next step. */
  workerJobEnded?: boolean;
  /** The quest that job was on, whose chat asks what comes next. */
  workerQuestId?: string | null;
}

export const CELEBRATION_THROTTLE_MS = 3000;

let handler: ((e: CelebrationEvent) => void) | null = null;
/** `${userId}|${key}` — per user, so a sign-out/sign-in in one tab doesn't swallow B's events. */
const emitted = new Set<string>();
let lastEmitAt = -Infinity;
let held: { event: CelebrationEvent; userId: string | undefined } | null = null;
let heldTimer: ReturnType<typeof setTimeout> | null = null;

function emit(e: CelebrationEvent) {
  lastEmitAt = Date.now();
  try {
    handler?.(e);
  } catch (err) {
    console.debug("[celebrations] handler failed", err);
  }
}

function currentUserId(): string | undefined {
  return useAuthStore.getState().session?.userId;
}

function flushHeld() {
  heldTimer = null;
  const h = held;
  held = null;
  // Held for someone who has since signed out: it isn't theirs to see.
  if (h && h.userId === currentUserId()) emit(h.event);
}

export function celebrate(
  kind: CelebrationKind,
  entityId: string,
  source: "action" | "notification" = "action",
  opts: { workerJobEnded?: boolean; workerQuestId?: string | null } = {},
): void {
  try {
    const key = celebrationKey(kind, entityId);
    const userId = currentUserId();
    const scoped = `${userId ?? ""}|${key}`;
    if (emitted.has(scoped)) return;
    emitted.add(scoped);
    if (userId) markSeen(userId, [key]);

    const event: CelebrationEvent = {
      kind,
      entityId,
      key,
      source,
      workerJobEnded: opts.workerJobEnded,
      workerQuestId: opts.workerQuestId,
    };
    const wait = lastEmitAt + CELEBRATION_THROTTLE_MS - Date.now();
    if (wait <= 0 && !held) {
      emit(event);
      return;
    }
    // Inside the window: hold ONE, and let the bigger moment take the slot.
    if (!held || celebrationRank(kind) > celebrationRank(held.event.kind)) held = { event, userId };
    heldTimer ??= setTimeout(flushHeld, Math.max(wait, 0));
  } catch (err) {
    console.debug("[celebrations] celebrate failed", err);
  }
}

export function subscribeCelebrations(next: (e: CelebrationEvent) => void): () => void {
  handler = next;
  return () => {
    if (handler === next) handler = null;
  };
}

export function __resetCelebrationBusForTests(): void {
  handler = null;
  emitted.clear();
  lastEmitAt = -Infinity;
  held = null;
  if (heldTimer) clearTimeout(heldTimer);
  heldTimer = null;
}
